import React, { useEffect } from 'react';
import { useAnalytics } from '../context/AnalyticsContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './Terms.css';

const Terms: React.FC = () => {
  const { trackPageView } = useAnalytics();

  useEffect(() => {
    trackPageView('/terms', 'Terms and Conditions');
  }, [trackPageView]);

  return (
    <div className="terms-page">
      <Header />
      
      <main className="terms-content">
        <div className="terms-container">
          <h1>Terms and Conditions</h1>
          <p className="last-updated">Last Updated: October 22, 2025</p>

          <section className="terms-section">
            <p>
              Welcome to Aika Camp Planner ("Aika," "we," "our," or "us"). By using our website, platform, or related services (collectively, the "Service"), you agree to the following Terms and Conditions. Please read them carefully before using Aika.
            </p>
          </section>

          <section className="terms-section">
            <h2>1. Purpose of the Service</h2>
            <p>
              Aika Camp Planner provides an online platform that helps families discover, compare, and organize camp options for children.
            </p>
            <p>
              Aika does not operate, endorse, or take responsibility for any of the camps or programs listed on our platform.
            </p>
            <p>
              All information about camps, including descriptions, schedules, and pricing, is provided by the camps themselves or obtained from publicly available sources.
            </p>
            <p>
              Aika simply displays this information to help users make informed choices.
            </p>
          </section>

          <section className="terms-section">
            <h2>2. No Liability for Camp Content or Experiences</h2>
            <p>
              Aika assumes no responsibility or liability for:
            </p>
            <ul>
              <li>The accuracy, completeness, or reliability of camp listings or their content.</li>
              <li>The safety, quality, availability, or performance of any camp or program.</li>
              <li>Any damages, losses, injuries, or disputes that arise from participation in or dealings with any camp or camp provider.</li>
            </ul>
            <p>
              All arrangements made between users and camps are independent of Aika. Users participate at their own discretion and risk.
            </p>
          </section>

          <section className="terms-section">
            <h2>3. Removal of Content and Accounts</h2>
            <p>
              Aika reserves the right to:
            </p>
            <ul>
              <li>Remove or modify any camp listing upon request from the camp, an authorized representative, or if we determine the content violates our policies or the law.</li>
              <li>Remove or deactivate any user account upon request from the user, or if we believe it violates these Terms or applicable laws.</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>4. User Accounts and Responsibilities</h2>
            <p>
              Users are responsible for maintaining the confidentiality of their account credentials and for all activities that occur under their account.
            </p>
            <p>
              Users agree not to use Aika for unlawful purposes, to upload misleading information, or to attempt unauthorized access to the platform.
            </p>
          </section>

          <section className="terms-section">
            <h2>5. Data and Privacy</h2>
            <p>
              Aika stores user information such as names, emails, preferences, and camp planning data for the purpose of providing and improving the Service.
            </p>
            <p>
              We do not sell, rent, or share personal data with third-party providers, except as required by law or necessary to operate the platform (e.g., secure data hosting).
            </p>
            <p>
              For details on how we handle your data, please refer to our Privacy Policy.
            </p>
          </section>

          <section className="terms-section">
            <h2>6. Intellectual Property</h2>
            <p>
              All Aika software, design, and content (except camp listings) are owned by Aika Camp Planner and protected by copyright and trademark laws.
            </p>
            <p>
              You may use the platform only for personal, non-commercial purposes.
            </p>
          </section>

          <section className="terms-section">
            <h2>7. Disclaimer of Warranties</h2>
            <p>
              Aika is provided "as is" and "as available" without warranties of any kind, either express or implied.
            </p>
            <p>
              We make no guarantees that the Service will be error-free, uninterrupted, or that all camp information will be accurate or current.
            </p>
          </section>

          <section className="terms-section">
            <h2>8. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, Aika and its owners, employees, and partners shall not be liable for any direct, indirect, incidental, consequential, or special damages arising from or related to:
            </p>
            <ul>
              <li>Your use or inability to use the Service,</li>
              <li>Reliance on any camp information, or</li>
              <li>Interactions with any camp or other user.</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>9. Changes to These Terms</h2>
            <p>
              Aika may update these Terms from time to time. Updated versions will be posted on our website with a revised "Effective Date." Continued use of the platform after changes are posted constitutes acceptance of the new Terms.
            </p>
          </section>

          <section className="terms-section">
            <h2>10. Contact Information</h2>
            <p>
              For questions or requests (including data removal or account deletion), contact us at:
            </p>
            <p>
              📧 support@aika.camp
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Terms;
